import { v4 } from 'uuid';
import { checkAvailability, getItem, setItem } from '@monterosa-sdk/util';

var version = "0.17.1";

/**
 * @license
 * constants.ts
 * core
 *
 * Created by Rygor Kharytanovich <rygor@monterosa.co.uk> on 2023-03-21
 * Copyright © 2023 Monterosa. All rights reserved.
 *
 * More details on the license can be found at https://www.monterosa.co/sdk/license
 */
/**
 * Default sdk name.
 *
 * @internal
 */
const DEFAULT_SDK_NAME = '[DEFAULT]';
/**
 * The current SDK version.
 *
 * @public
 */
const VERSION = version;
/**
 * Device ID storage key name
 *
 * @internal
 */
const DEVICE_ID_KEY = 'device_id';

/**
 * @license
 * sdk.ts
 * core
 *
 * Created by Rygor Kharytanovich <rygor@monterosa.co.uk> on 2021-09-23
 * Copyright © 2022 Monterosa. All rights reserved.
 *
 * More details on the license can be found at https://www.monterosa.co/sdk/license
 */
/**
 * @internal
 */
class Sdk {
    constructor(options, name) {
        this._isDestroyed = false;
        this._options = Object.assign({}, options);
        this._name = name;
        this._kits = new Map();
    }
    get name() {
        this.checkDestroyed();
        return this._name;
    }
    get options() {
        this.checkDestroyed();
        return this._options;
    }
    get isDestroyed() {
        return this._isDestroyed;
    }
    get kits() {
        return this._kits;
    }
    set isDestroyed(value) {
        this._isDestroyed = value;
    }
    deleteKits() {
        this.kits.forEach((kit, name) => {
            this.kits.delete(name);
            kit.delete();
        });
    }
    checkDestroyed() {
        if (this.isDestroyed) {
            throw new Error(`SDK named ${this.name} destroyed`);
        }
    }
}
/**
 * @internal
 */
function addKit(sdk, name, kit) {
    sdk.kits.set(name, kit);
}
/**
 * @internal
 */
function getKit(sdk, name) {
    return sdk.kits.get(name);
}
/**
 * @internal
 */
function deleteKit(sdk, name) {
    sdk.kits.delete(name);
}

/**
 * @license
 * api.ts
 * core
 *
 * Created by Rygor Kharytanovich <rygor@monterosa.co.uk> on 2022-02-15
 * Copyright © 2022 Monterosa. All rights reserved.
 *
 * More details on the license can be found at https://www.monterosa.co/sdk/license
 */
const sdks = new Map();
let DEVICE_ID;
/**
 * An array of all configured sdks.
 *
 * @public
 */
function getSdks() {
    return Array.from(sdks.values());
}
/**
 * Retrieves an SDK instance by its name.
 *
 * @example
 * ```javascript
 * const sdk = getSdk('my-sdk');
 * ```
 *
 * @param name - Optional name of the sdk to initialise.
 *   If name is not provided, the default is "[DEFAULT]"
 *
 * @public
 */
function getSdk(name = DEFAULT_SDK_NAME) {
    const sdk = sdks.get(name);
    if (!sdk) {
        throw new Error(`No Monterosa Sdk "${name}" has been configured.
        You need to call configure() first`);
    }
    return sdk;
}
/**
 * @internal
 */
function getDeviceId() {
    if (DEVICE_ID !== undefined) {
        return DEVICE_ID;
    }
    if (!checkAvailability()) {
        DEVICE_ID = v4();
        return DEVICE_ID;
    }
    const value = getItem(DEVICE_ID_KEY);
    if (value === null) {
        DEVICE_ID = v4();
        setItem(DEVICE_ID_KEY, DEVICE_ID);
    }
    else {
        DEVICE_ID = value;
    }
    return DEVICE_ID;
}
/**
 * Creates an SDK instance.
 *
 * @example
 *
 * Configure default sdk with host and project id
 *
 * ```javascript
 * configure({
 *   host: "...",
 *   projectId: "..."
 * });
 * ```
 *
 * @param options - Studio host
 * @param name - Optional name of the sdk to initialise.
 *   If name is not provided, the default is "[DEFAULT]"
 *
 * @returns Configured SDK instance.
 *
 * @public
 */
function configure(options, name = DEFAULT_SDK_NAME) {
    const existingSdk = sdks.get(name);
    if (existingSdk) {
        return existingSdk;
    }
    const sdk = new Sdk(options, name);
    sdks.set(name, sdk);
    return sdk;
}
/**
 * Destroy initialised sdk
 *
 * @example
 * ```javascript
 * const sdk = configure(...);
 *
 * destroy(sdk);
 * ```
 *
 * @param sdk - sdk instance
 */
function destroy(sdk) {
    const { name } = sdk;
    sdks.delete(name);
    sdk.deleteKits();
    sdk.isDestroyed = true;
}

/**
 * @license
 * logger.ts
 * core
 *
 * Created by Rygor Kharytanovich <rygor@monterosa.co.uk> on 2022-05-31
 * Copyright © 2022 Monterosa. All rights reserved.
 *
 * More details on the license can be found at https://www.monterosa.co/sdk/license
 */
/* eslint-disable no-console */
/**
 * Represents the possible log levels.
 */
var LogLevel;
(function (LogLevel) {
    /**
     * Verbose level. Provides detailed and extensive logging.
     */
    LogLevel[LogLevel["Verbose"] = 0] = "Verbose";
    /**
     * Debug level. Used for debugging and development purposes.
     */
    LogLevel[LogLevel["Debug"] = 1] = "Debug";
    /**
     * Info level. Used to provide general information about the system's state.
     */
    LogLevel[LogLevel["Info"] = 2] = "Info";
    /**
     * Warn level. Indicates potential issues or situations that might cause problems in the future.
     */
    LogLevel[LogLevel["Warn"] = 3] = "Warn";
    /**
     * Error level. Indicates critical errors and issues that require immediate attention.
     */
    LogLevel[LogLevel["Error"] = 4] = "Error";
    /**
     * Silent level. No logs will be output.
     */
    LogLevel[LogLevel["Silent"] = 5] = "Silent";
})(LogLevel || (LogLevel = {}));
const Methods = {
    [LogLevel.Verbose]: 'log',
    // `console.debug` is not shown in Chrome and Edge by default. It must be
    // enabled manually. To avoid hassle for developers with settings debug
    // messages are sent to `console.log`
    [LogLevel.Debug]: 'log',
    [LogLevel.Info]: 'info',
    [LogLevel.Warn]: 'warn',
    [LogLevel.Error]: 'error',
};
const logHandler = (logger, logLevel, ...args) => {
    if (logger.logLevel > logLevel) {
        return;
    }
    const method = Methods[logLevel];
    console[method](new Date().toISOString().replace(/[TZ]/gi, ' ').trim(), `${LogLevel[logLevel]}/${logger.name}:`, ...args);
};
/** @internal */
class Logger {
    constructor(name) {
        this.name = name;
        this._logLevel = LogLevel.Warn;
        this._logHandler = logHandler;
    }
    get logLevel() {
        return this._logLevel;
    }
    set logLevel(level) {
        this._logLevel = level;
    }
    get logHandler() {
        return this._logHandler;
    }
    set logHandler(handler) {
        this.logHandler = handler;
    }
    log(...args) {
        this.logHandler(this, LogLevel.Verbose, ...args);
    }
    debug(...args) {
        this.logHandler(this, LogLevel.Debug, ...args);
    }
    info(...args) {
        this.logHandler(this, LogLevel.Info, ...args);
    }
    warn(...args) {
        this.logHandler(this, LogLevel.Warn, ...args);
    }
    error(...args) {
        this.logHandler(this, LogLevel.Error, ...args);
    }
}

export { DEFAULT_SDK_NAME, DEVICE_ID_KEY, LogLevel, Logger, Sdk, VERSION, addKit, configure, deleteKit, destroy, getDeviceId, getKit, getSdk, getSdks };
//# sourceMappingURL=index.esm2017.js.map
